import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { Tabs } from 'components/FbUI/Tabs'
import { MobileListViewHeader } from 'components/MobileListViewHeader'
import { MobileInfiniteScrollView } from 'components/MobileInfiniteScrollView'
import { MobileColors } from 'styles/mobileTokens'
import { formatInteger } from 'utils/formatting'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompanyType } from 'models/contact_companies'
import { getIdWithPrefix } from 'utils/pathUtils'
import useApiService from 'services/api'
import { fetchUnifiedRestaurantDoors } from 'services/opportunities/useUnifiedDoorsReportsTable'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { BackButton } from 'components/Mobile/Mobile.styles'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: white;
`

const HeaderSection = styled.div`
  padding: 16px;
  background: white;
`

const HeaderLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 4px;
`

const ChainName = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 4px 0;
`

const LocationCount = styled.div`
  font-size: 14px;
  color: #6b7280;
`

const DoorItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;

  &:active {
    background: #f3f4f6;
  }
`

const DoorInfo = styled.div`
  flex: 1;
`

const DoorName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #111827;
`

const DoorAddress = styled.div`
  font-size: 12px;
  color: ${MobileColors.primary};
`

const DoorsListContainer = styled.div`
  width: 100%;
`

const FILTER_IDENTIFIER = 'MOBILE_DOORS_LIST' as const
const PAGE_SIZE = 50

export function MobileDoorsListPage() {
  const navigate = useNavigate()
  const urlParams = useParams()
  const { preferences } = usePreferences()
  const api = useApiService()

  const [selectedTab, setSelectedTab] = useState<'firstbite' | 'unmatched'>(
    'firstbite'
  )

  const chainName = urlParams?.chain_name
  const chainID = chainName
    ? getIdWithPrefix(
        chainName,
        preferences?.company_type_prefix_map[ContactCompanyType.Chain]
      )
    : undefined

  // Chain data: gives you chainData.count and contact_company_id
  const { data: chainData, isLoading: isLoadingChain } =
    api.useGetChain(chainID)
  const chainProxyId = chainData?.contact_company_id

  // First Bite count: mirror DoorsTableTabs: totalCount = chainData.count
  const firstbiteCount = chainData?.count || 0

  // Filters
  const filterStore = filterStoreRepo.getStore(FILTER_IDENTIFIER)
  const filtersAsParams = useFilterParams(filterStore)

  const baseFilterParams = useMemo(() => {
    const params: Record<string, unknown> = { ...filtersAsParams }
    if (chainID) {
      params.chain = chainID
    }
    return params
  }, [filtersAsParams, chainID])

  // ---------- First Bite list (matched / reports) ----------

  const firstbiteParams = useMemo(
    () => ({
      ...baseFilterParams,
      type: 'all' as const,
    }),
    [baseFilterParams]
  )

  const firstbiteQuery = useInfiniteQuery({
    queryKey: ['mobile-doors-firstbite', firstbiteParams],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await fetchUnifiedRestaurantDoors({
        ...firstbiteParams,
        page: pageParam,
        limit: PAGE_SIZE,
      })
      return response
    },
    initialPageParam: 1,
    // The unified doors list paginates without a total count (results-only
    // response), so page while full pages keep coming.
    getNextPageParam: (lastPage, allPages) =>
      lastPage.results.length === PAGE_SIZE ? allPages.length + 1 : undefined,
    enabled: !!chainID && selectedTab === 'firstbite',
  })

  // ---------- Unmatched summary (alt_count via door proxies summary) ----------

  const unmatchedSummaryQuery = useQuery({
    queryKey: ['mobile-doors-unmatched-summary', chainProxyId],
    queryFn: async () => {
      if (!chainProxyId) {
        return { count: 0, alt_count: 0 }
      }
      return api.getDoorProxiesOfChainProxySummary(chainProxyId, {
        type: 'all',
      })
    },
    enabled: !!chainProxyId,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
  })

  const unmatchedCount = unmatchedSummaryQuery.data?.alt_count || 0

  // ---------- Unmatched list (door proxies) ----------

  const unmatchedQuery = useInfiniteQuery({
    queryKey: ['mobile-doors-unmatched', chainProxyId, filtersAsParams],
    queryFn: async ({ pageParam = 1 }) => {
      if (!chainProxyId) {
        return { results: [], count: 0 }
      }
      const response = await api.getDoorProxiesOfChainProxy(chainProxyId, {
        ...filtersAsParams,
        page: pageParam,
        limit: PAGE_SIZE,
        type: 'all',
      })
      return response
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, allPages) => {
      const totalFetched = allPages.reduce(
        (sum: number, page: any) => sum + page.results.length,
        0
      )
      return totalFetched < lastPage.count ? allPages.length + 1 : undefined
    },
    enabled: !!chainProxyId && selectedTab === 'unmatched',
  })

  const handleBack = () => {
    navigate(-1)
  }

  const handleDoorClick = (doorId: number) => {
    navigate(`/restaurant/${doorId}`)
  }

  const activeQuery =
    selectedTab === 'firstbite' ? firstbiteQuery : unmatchedQuery

  return (
    <PageContainer>
      <BackButton onClick={handleBack}>
        <ChevronLeft size={20} />
        <span>Back</span>
      </BackButton>

      <HeaderSection>
        <HeaderLabel>DOORS</HeaderLabel>
        <ChainName>{chainData?.chain || 'Loading...'}</ChainName>
        <LocationCount>
          {isLoadingChain
            ? 'Loading...'
            : `${formatInteger(firstbiteCount)} locations`}
        </LocationCount>
      </HeaderSection>

      <div className="px-4 mt-2">
        <Tabs
          tabs={[
            {
              key: 'firstbite',
              title: `First Bite (${formatInteger(firstbiteCount)})`,
            },
            {
              key: 'unmatched',
              title: `Unmatched (${formatInteger(unmatchedCount)})`,
            },
          ]}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
      </div>

      <MobileListViewHeader
        sortingOptions={[]}
        filterStoreIdentifier={FILTER_IDENTIFIER}
        tableKey={FILTER_IDENTIFIER}
      />

      <FilterChips
        store={filterStore}
        identifier={FILTER_IDENTIFIER}
        clearAllButton
        showActive
      />

      <MobileInfiniteScrollView
        infiniteQuery={activeQuery}
        extractItems={(page) => page.results}
        className="flex-1 overflow-auto flex-col"
      >
        {(items) => (
          <DoorsListContainer>
            {items.map((door: any) => (
              <DoorItem
                key={
                  door.restaurant?.firstbite_id || door.door_proxy_id || door.id
                }
                onClick={() =>
                  handleDoorClick(
                    door.restaurant?.firstbite_id ||
                      door.door_proxy_id ||
                      door.id
                  )
                }
              >
                <DoorInfo>
                  <DoorName>{door.name}</DoorName>
                  <DoorAddress>
                    {/* Unified door rows carry the street line as
                        `street_address`; unmatched door-proxy rows as `address`. */}
                    {door.address ?? door.street_address}, {door.city},{' '}
                    {door.state} {door.zipcode}
                  </DoorAddress>
                </DoorInfo>
                <ChevronRight size={20} color="#9CA3AF" />
              </DoorItem>
            ))}
          </DoorsListContainer>
        )}
      </MobileInfiniteScrollView>
    </PageContainer>
  )
}
